import { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { IconInfoSquareRounded } from '@rossum/ui/icons/tabler';
import {
  Paper,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from '@rossum/ui/material';

const GRAPH_HEIGHT = 400;

type GraphName =
  | 'turnaroundAvgS'
  | 'timePerDocumentAvgS'
  | 'correctionsPerDocumentAvg'
  | 'delay'
  | 'automated'
  | 'rejection'
  | 'timeAndCorrectionsTable'
  | 'usage';

type GraphContainerProps = {
  children: ReactNode;
  graphName?: GraphName;
  fullWidth?: boolean;
};

const GraphContainer = ({
  children,
  graphName,
  fullWidth,
}: GraphContainerProps) => {
  const intl = useIntl();

  return (
    <Stack
      component={Paper}
      position="relative"
      justifyContent="flex-start"
      height={GRAPH_HEIGHT}
      p={2}
      sx={{
        width: t => (fullWidth ? '100%' : `calc(50% - ${t.spacing(1)})`),
      }}
    >
      {graphName && (
        <Stack direction="row" mb={2} spacing={0.5}>
          <Typography variant="body2" fontWeight={600}>
            {intl.formatMessage({
              id: `components.graphs.${graphName}.title`,
            })}
          </Typography>
          <Tooltip
            title={intl.formatMessage(
              { id: `components.graphs.${graphName}.tooltip` },
              {
                linebreak: <br />,
                boldText: (chunks: ReactNode[]) => <strong>{chunks}</strong>,
              }
            )}
            placement="top"
            componentsProps={{
              tooltip: { sx: { textAlign: 'left' } },
            }}
          >
            <SvgIcon fontSize="medium" color="disabled">
              <IconInfoSquareRounded />
            </SvgIcon>
          </Tooltip>
        </Stack>
      )}
      <Stack
        height={1}
        sx={{ maxHeight: GRAPH_HEIGHT - 60 }}
        justifyContent="space-between"
      >
        {children}
      </Stack>
    </Stack>
  );
};

export default GraphContainer;
